import React, { useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { MAX_INSTRUCTION_LENGTH } from "../../../utils/presentationDraft";

interface RegeneratePresentationDraftDialogProps {
	readonly open: boolean;
	readonly isSubmitting: boolean;
	readonly onClose: () => void;
	readonly onSubmit: (instruction: string) => void;
}

const QUICK_INSTRUCTION_KEYS = ["shorter", "moreResults", "moreDirect", "highlightLeadership"] as const;

const RegeneratePresentationDraftDialog: React.FC<RegeneratePresentationDraftDialogProps> = ({
	open,
	isSubmitting,
	onClose,
	onSubmit
}) => {
	const { t } = useTranslation();
	const [instruction, setInstruction] = useState("");
	const [submitted, setSubmitted] = useState(false);

	const trimmed = instruction.trim();
	const error = (() => {
		if (!submitted) return undefined;
		if (!trimmed) return t("presentationDraft.regenerate.errors.required");
		if (trimmed.length > MAX_INSTRUCTION_LENGTH) {
			return t("presentationDraft.regenerate.errors.tooLong", { max: MAX_INSTRUCTION_LENGTH });
		}
		return undefined;
	})();

	const appendQuickInstruction = (text: string) =>
		setInstruction(current => (current.trim() ? `${current.trim()}\n${text}` : text));

	const handleSubmit = () => {
		setSubmitted(true);
		if (!trimmed || trimmed.length > MAX_INSTRUCTION_LENGTH) return;
		onSubmit(trimmed);
	};

	return (
		<Dialog open={open} onClose={isSubmitting ? undefined : onClose} fullWidth maxWidth="sm">
			<DialogTitle>{t("presentationDraft.regenerate.title")}</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
					{QUICK_INSTRUCTION_KEYS.map(key => (
						<Chip
							key={key}
							label={t(`presentationDraft.regenerate.quick.${key}`)}
							onClick={() => appendQuickInstruction(t(`presentationDraft.regenerate.quick.${key}`))}
							variant="outlined"
							color="secondary"
						/>
					))}
				</Box>
				<TextField
					label={t("presentationDraft.regenerate.instruction")}
					placeholder={t("presentationDraft.regenerate.placeholder")}
					value={instruction}
					onChange={event => setInstruction(event.target.value)}
					multiline
					minRows={3}
					fullWidth
					required
					error={!!error}
					helperText={error ?? `${trimmed.length}/${MAX_INSTRUCTION_LENGTH}`}
					slotProps={{ inputLabel: { shrink: true } }}
				/>
				<Alert severity="info">{t("presentationDraft.regenerate.keep")}</Alert>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2.5 }}>
				<Button onClick={onClose} disabled={isSubmitting}>
					{t("common.cancel")}
				</Button>
				<Button variant="contained" color="secondary" onClick={handleSubmit} disabled={isSubmitting}>
					{t("presentationDraft.regenerate.submit")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default RegeneratePresentationDraftDialog;
